import { useEffect, useState, useRef } from "react";
import {
  GpsFixed,
  GpsFixedDisabled,
  supportAgent,
  supportAgentDisabled,
  Verified,
  VerifiedDisabled,
} from "assets/images";
import Drawer from "react-modern-drawer";
import {
  useChatIndexQuery,
  useLazyGeoLocationLiveQuery,
  useReadAllMessageMutation,
  useTrackerIndexQuery,
} from "redux/RTQ/IlenkradApi";
import LoadingLottiePc from "components/lottieLoading/LoadingLottiePc";
import ChatPc from "./ChatPc";
import { useNavigate } from "react-router-dom";
import TrackerAlertModals from "components/v2/Modals/TrackerAlertModals/TrackerAlertModals";
import { useSelector } from "react-redux";
import classes from "pages/Home/notification/Notification.module.scss";

interface NotificationPcProps {
  closeDrawer: () => void;
}

const NotificationPc: React.FC<NotificationPcProps> = ({ closeDrawer }) => {
  const drawerState = useSelector((state: any) => state.drawer);
  const [chatId, setChatId] = useState<any>();
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState<boolean>(false);
  const [currentTracker, setCurrentTracker] = useState<any>({});
  const [isExpiredTrackersModalOpen, setIsExpiredTrackersModalOpen] =
    useState<boolean>(false);
  const [currentTrackers, setCurrentTrackers] = useState<any>([]);
  const prevChatDrawerStateRef = useRef<boolean>(false);

  // Listen to Redux state for chat drawer trigger
  useEffect(() => {
    if (
      drawerState.isChatDrawerOpen &&
      !prevChatDrawerStateRef.current &&
      drawerState.selectedChatId
    ) {
      setChatId(drawerState.selectedChatId);
      setTimeout(() => {
        setIsOpen(true);
      }, 500);
    }
    prevChatDrawerStateRef.current = drawerState.isChatDrawerOpen;
  }, [drawerState.isChatDrawerOpen, drawerState.selectedChatId]);

  const {
    data: dataChat,
    isLoading: isLoadingChat,
    refetch,
  } = useChatIndexQuery(undefined, {
    pollingInterval: 10000,
    refetchOnFocus: true,
    refetchOnReconnect: true,
  });

  const [mutateReadAll, { isLoading: isLoadingReadAll }] =
    useReadAllMessageMutation();
  const [mutateLocationLive, { data: dataLocationLive }] =
    useLazyGeoLocationLiveQuery();
  const { data: dataAllTracker } = useTrackerIndexQuery();

  useEffect(() => {
    mutateLocationLive();
    refetch();
  }, []);

  const navigate = useNavigate();

  const openPaymentModal = (tracker: any) => {
    setCurrentTracker(tracker);
    setIsPaymentModalOpen(true);
  };

  const closePaymentModal = () => {
    setIsPaymentModalOpen(false);
    closeDrawer();
  };

  const openExpiredTrackersModal = (trackers: any) => {
    setCurrentTrackers(trackers);
    setIsExpiredTrackersModalOpen(true);
  };

  const closeExpiredTrackersModal = () => {
    setIsExpiredTrackersModalOpen(false);
    closeDrawer();
  };

  const navigateToPlans = () => {
    navigate("/plans");
    closeDrawer();
  };

  const submitReadAll = () => {
    if (!dataLocationLive?.geo_locations) {
      mutateReadAll("");
      return;
    }
    if (
      Object.values(dataLocationLive.geo_locations).some(
        (live: any) => live?.expired === true,
      )
    ) {
      const liveLocations = dataLocationLive.geo_locations as Record<
        string,
        any
      >;
      const expiredDevicesImeis = Object.values(liveLocations)
        .filter((live) => live?.expired)
        .map((live) => live?.imei?.toString());

      openExpiredTrackersModal(
        (dataAllTracker?.trackers || []).filter((tracker: any) =>
          expiredDevicesImeis.includes(tracker.imei),
        ),
      );
    } else {
      mutateReadAll("");
    }
  };

  const [isOpen, setIsOpen] = useState<boolean>(false);
  const closeDrawerChat = () => {
    setIsOpen(false);
    closeDrawer();
  };

  const navigateToChat = (item: any) => {
    if (dataLocationLive?.geo_locations[item.tracker_imei]?.expired) {
      openPaymentModal(
        dataAllTracker?.trackers?.filter(
          (tracker: any) => tracker.imei === item.tracker_imei,
        )[0],
      );
    } else {
      setChatId(item?.id);
      setIsOpen(true);
    }
  };

  const formatChatTime = (dateStr: string) => {
    try {
      const d = new Date(dateStr);
      const today = new Date();
      if (d.toDateString() === today.toDateString()) {
        return d.toLocaleTimeString("fa-IR", {
          hour: "2-digit",
          minute: "2-digit",
          hour12: false,
        });
      }
      return d.toLocaleDateString("fa-IR", {
        month: "numeric",
        day: "numeric",
      });
    } catch {
      return "";
    }
  };

  const getChatSubtitle = (item: any) => {
    if (item.title?.includes("پشتیبانی")) {
      return "کارشناس پشتیبانی";
    }
    if (item.tracker_imei) {
      return `ردیاب فعال (${item.tracker_imei.slice(-4)})`;
    }
    return "پیام‌های گفتگو";
  };

  const getChatIcon = (item: any, isExpired: boolean) => {
    if (item.title?.includes("پشتیبانی")) {
      return isExpired ? supportAgentDisabled : supportAgent;
    }
    if (item.title?.includes("ردیاب")) {
      return isExpired ? GpsFixedDisabled : GpsFixed;
    }
    return isExpired ? VerifiedDisabled : Verified;
  };

  if (isLoadingChat || isLoadingReadAll) {
    return <LoadingLottiePc />;
  }

  const chats = dataChat?.chats || [];

  return (
    <>
      <Drawer
        open={isOpen}
        direction="left"
        size={400}
        overlayOpacity={0.7}
        zIndex={99999}
      >
        {isOpen ? <ChatPc closeDrawer={closeDrawerChat} id={chatId} /> : null}
      </Drawer>

      <div className={classes.container}>
        {/* Top Header */}
        <header className={classes.header}>
          <div className={classes.headerRight}>
            <h1 className={classes.headerTitle}>پیام‌ها</h1>
          </div>

          <div className={classes.headerLeft}>
            <button
              type="button"
              className={classes.backButton}
              onClick={closeDrawer}
              aria-label="بستن"
              title="بستن"
            >
              <div className="icon3-close" />
            </button>
          </div>
        </header>

        {/* WhatsApp / Telegram Chat List */}
        <div className={classes.chatList}>
          {chats.length === 0 && (
            <div className={classes.emptyState}>
              <span>پیامی برای نمایش وجود ندارد</span>
            </div>
          )}

          {chats.map((item: any) => {
            const isExpired =
              Boolean(
                item.tracker_imei &&
                  dataLocationLive?.geo_locations?.[item.tracker_imei]?.expired,
              );

            return (
              <div
                key={item?.id}
                onClick={() => navigateToChat(item)}
                className={`${classes.chatItem} ${isExpired ? classes.disabled : ""}`}
              >
                <div className={classes.avatar}>
                  <img
                    alt={item.title || "chat avatar"}
                    src={getChatIcon(item, isExpired)}
                  />
                </div>

                <div className={classes.itemInfo}>
                  <div className={classes.topRow}>
                    <span className={classes.itemTitle}>{item?.title}</span>
                    <span className={classes.itemTime}>
                      {formatChatTime(item.updated_at)}
                    </span>
                  </div>

                  <div className={classes.bottomRow}>
                    <span className={classes.itemSubtitle}>
                      {getChatSubtitle(item)}
                    </span>
                    {item?.new_messages > 0 && !isExpired && (
                      <span className={classes.unreadBadge}>
                        {item.new_messages}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <TrackerAlertModals
        trackers={currentTrackers}
        isPaymentModalOpen={isExpiredTrackersModalOpen}
        closePaymentModal={closeExpiredTrackersModal}
        onActionOne={navigateToPlans}
      />
      <TrackerAlertModals
        tracker={currentTracker}
        isPaymentModalOpen={isPaymentModalOpen}
        closePaymentModal={closePaymentModal}
        onActionOne={navigateToPlans}
      />
    </>
  );
};

export default NotificationPc;
